import React from 'react';
import { AlertTriangle, ShieldOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AckRequirement, InsecureTransportAckRecord, formatAckDate } from './insecureTransport';

/**
 * Warning and acceptance checkbox shown when a database connector is set to an
 * unencrypted connection (TLS mode disable). See insecureTransport.ts.
 */
export const InsecureTransportAck: React.FC<{
  requirement: AckRequirement;
  /** The connector already runs unencrypted with an active acknowledgment. */
  record?: InsecureTransportAckRecord | null;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ requirement, record, checked, onChange }) => {
  const { t } = useTranslation();
  const accepted = requirement === 'none' && record && record.active !== false;
  if (requirement === 'none' && !accepted) return null;

  return (
    <div
      data-testid="insecure-transport-ack"
      role="alert"
      style={{
        marginBottom: 14, padding: '10px 12px', borderRadius: 4, fontSize: 12, lineHeight: 1.5,
        border: '1px solid var(--color-status-red-border)',
        backgroundColor: 'var(--color-status-red-dim)', color: 'var(--color-text)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: 'var(--color-status-red)', marginBottom: 4 }}>
        <ShieldOff size={14} />
        {t('connectors.insecureTransport.title', 'Conexión sin cifrar')}
      </div>
      <div style={{ color: 'var(--color-text-muted)' }}>
        {t('connectors.insecureTransport.body',
          'Con el modo disable, el usuario y la contraseña de la base y todos los resultados de las consultas viajan sin cifrar entre maic y el servidor. Cualquiera que pueda observar esa red podría leerlos. Úsalo solo si el servidor no admite TLS y la red es de confianza.')}
      </div>

      {accepted ? (
        <div data-testid="insecure-transport-accepted" style={{ marginTop: 8, color: 'var(--color-text-muted)' }}>
          {t('connectors.insecureTransport.acceptedBy', {
            by: record?.by ?? '—', at: formatAckDate(record?.at),
            defaultValue: 'Riesgo aceptado por {{by}} el {{at}}.',
          })}
        </div>
      ) : (
        <label
          style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10, cursor: 'pointer', color: 'var(--color-text)' }}
        >
          <input
            type="checkbox"
            data-testid="insecure-transport-ack-checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            style={{ marginTop: 2, flexShrink: 0, accentColor: 'var(--color-status-red)' }}
          />
          <span>
            {t('connectors.insecureTransport.checkbox',
              'Entiendo que con esta opción las credenciales y los datos viajan sin cifrar entre maic y nuestra base de datos, y acepto ese riesgo.')}
          </span>
        </label>
      )}

      {requirement === 'required' && !checked && (
        <div data-testid="insecure-transport-ack-needed" style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: 6, fontSize: 11, color: 'var(--color-status-red)' }}>
          <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{t('connectors.insecureTransport.needed', 'Para guardar una conexión sin cifrar hay que aceptar el riesgo.')}</span>
        </div>
      )}
      {requirement === 'optional' && (
        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--color-text-muted)' }}>
          {t('connectors.insecureTransport.optional',
            'Este conector ya funcionaba sin cifrar y no tiene una aceptación registrada. Marca la casilla para dejarla registrada a tu nombre.')}
        </div>
      )}
    </div>
  );
};

/** "Sin cifrar" badge for a database connector in plaintext, with who accepted it. */
export const UnencryptedBadge: React.FC<{
  record?: InsecureTransportAckRecord | null;
  compact?: boolean;
}> = ({ record, compact }) => {
  const { t } = useTranslation();
  const active = record && record.active !== false;
  const title = active
    ? t('connectors.insecureTransport.badgeTitleAccepted', {
      by: record?.by ?? '—', at: formatAckDate(record?.at),
      defaultValue: 'Conexión sin cifrar. Riesgo aceptado por {{by}} el {{at}}.',
    })
    : t('connectors.insecureTransport.badgeTitleNoRecord', 'Conexión sin cifrar, sin aceptación registrada.');
  return (
    <span
      data-testid="unencrypted-badge"
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
        fontSize: 11, fontWeight: 500, height: 18, padding: compact ? '0 5px' : '0 6px', borderRadius: 3,
        color: 'var(--color-status-red)', backgroundColor: 'var(--color-status-red-dim)',
        boxShadow: 'inset 0 0 0 1px var(--color-status-red-border)',
      }}
    >
      <ShieldOff size={11} />
      {t('connectors.insecureTransport.badge', 'Sin cifrar')}
    </span>
  );
};

/** One line under a connector's header: who accepted the plaintext connection. */
export const UnencryptedAckLine: React.FC<{ record?: InsecureTransportAckRecord | null }> = ({ record }) => {
  const { t } = useTranslation();
  const active = record && record.active !== false;
  return (
    <div data-testid="unencrypted-ack-line" style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
      {active
        ? t('connectors.insecureTransport.acceptedBy', {
          by: record?.by ?? '—', at: formatAckDate(record?.at),
          defaultValue: 'Riesgo aceptado por {{by}} el {{at}}.',
        })
        : t('connectors.insecureTransport.noRecord', 'Sin aceptación registrada: edita el conector para registrarla.')}
    </div>
  );
};
